<template>
  <v-row dense>
    <v-col cols="12">
      <v-text-field
        ref="street"
        v-model="form.street"
        name="street"
        :label="`${$t('PROFILE.STREET')} *`"
        :readonly="readonly"
        :error-messages="errors.street"
        :rules="getRulesByName('street')"
        @input="
          validateKeySafeDescr();
          updateValue('street', $event);
        "
      />
    </v-col>
    <v-col cols="12">
      <v-text-field
        v-model="form.name"
        name="name"
        :label="$t('PROFILE.NAME')"
        :readonly="readonly"
        counter="255"
        :error-messages="errors.name"
        :rules="getRulesByName('textLine')"
        @input="updateValue('name', $event)"
      />
    </v-col>
    <v-col cols="12">
      <v-switch
        v-model="form.elevator"
        name="elevator"
        :readonly="readonly"
        :error-messages="errors.elevator"
        :label="$t('BUILDINGS.ELEVATOR')"
        @change="updateValue('elevator', $event)"
      ></v-switch>
    </v-col>
    <v-col cols="12">
      <v-select
        v-model="form.type"
        name="type"
        :readonly="readonly"
        :items="buildingTypes"
        item-text="name"
        item-value="id"
        :error-messages="errors.type"
        :rules="rules.requiredSelectNotArray"
        :label="`${$t('ITEM.TYPE')} *`"
        @change="updateValue('type', $event)"
      />
    </v-col>
    <v-col cols="12">
      <select-possession-form
        v-model="form.possession_form_id"
        item-text="name"
        item-value="id"
        :label="`${$t('BUILDINGS.POSSESSION_FORM')}`"
        :error-messages="errors.possession_form_id"
        :readonly="readonly"
        @change="updateValue('possession_form_id', $event)"
      />
    </v-col>
    <v-col cols="12">
      <v-switch
        v-model="form.has_notruf"
        :readonly="readonly"
        :label="$t('BUILDINGS.NOTRUF_INCLUDED')"
        @change="updateValue('has_notruf', $event)"
      ></v-switch>
    </v-col>

    <v-col v-if="$can('sensitive-data')" cols="12">
      <v-switch
        v-model="form.isKeysafeHouse"
        :readonly="readonly"
        :disabled="isKeysafeHouseLinked"
        :label="$t('BUILDINGS.KEY_SAFE_HOUSE')"
        @change="updateValue('isKeysafeHouse', $event)"
      ></v-switch>
    </v-col>
    <v-col v-if="$can('sensitive-data') && form.isKeysafeHouse" cols="12">
      <v-textarea
        v-model="form.access_description"
        name="access_description"
        :readonly="readonly"
        outlined
        :rules="rules.accessDescription"
        :error-messages="errors.access_description"
        :label="$t('BUILDINGS.ACCESS_DESCRIPTION')"
        :counter="1000"
        rows="2"
        @input="updateValue('access_description', $event)"
      />
    </v-col>
    <v-col v-if="$can('sensitive-data') && form.isKeysafeHouse" cols="12">
      <v-textarea
        ref="keysafe_description"
        v-model="form.keysafe_description"
        name="keysafe_description"
        outlined
        rows="2"
        :readonly="readonly"
        :error-messages="errors.keysafe_description"
        :rules="rules.keysafe_description"
        :label="$t('BUILDINGS.KEY_SAFE_ACCESS_DESCRIPTION')"
        @input="
          validateStreet();
          updateValue('name', $event);
        "
      />
    </v-col>

    <v-col v-if="$can('sensitive-data') && form.isKeysafeHouse" cols="12">
      <v-text-field
        v-model="form.key_code"
        name="key_code"
        :label="$t('BUILDINGS.KEY_CODE')"
        :readonly="readonly"
        counter="15"
        outlined
        :error-messages="errors.name"
        :rules="getRulesByName('key_code')"
        @input="updateValue('key_code', $event)"
      />
    </v-col>

    <v-col v-if="$can('sensitive-data') && !form.isKeysafeHouse" cols="12">
      <v-select
        v-model="form.keysafe_house_building_id"
        :items="keysafeHouses"
        item-text="street"
        item-value="id"
        :readonly="readonly"
        :error-messages="errors.keysafe_house_building_id"
        clearable
        :label="$t('BUILDINGS.KEY_SAFE_ADDRESS')"
        @input="updateValue('keysafe_house_building_id', $event)"
      ></v-select>
    </v-col>

    <v-col cols="12">
      <lists-filter
        v-model="form.list_of_document_id"
        item-value="id"
        item-text="name"
        :readonly="readonly"
        :clearable="!readonly"
        :error-messages="errors.list_of_document_id"
        :label="`${$t('DOCUMENTS.ITEMS')} ${$t('LISTS.ITEM')}`"
        @input="updateValue('list_of_document_id', $event)"
      />
    </v-col>
  </v-row>
</template>

<script>
import {
  requiredTextLine,
  requiredSelectNotArray,
  textLine,
  textDescription,
  maxLength,
  notEmoji
} from '@/modules/_global/variables/rules.js';
import formEditable from '@/modules/_global/mixins/form-editable';
import SelectPossessionForm from '@/modules/buildings/components/form/SelectPossessionForm.vue';
import ListsFilter from '@/modules/documents_lists/components/forms/filters/ListsFilter';
import buildingTypes from '@/modules/buildings/components/mixins/buildings-types';

export default {
  name: 'BuildingForm',

  components: {
    ListsFilter,
    SelectPossessionForm
  },

  mixins: [formEditable, buildingTypes],

  props: {
    keysafeHouses: {
      type: Array,
      default: () => {}
    },
    keysafeHousesLinked: {
      type: Set,
      default: () => {}
    }
  },

  data() {
    return {
      form: {
        keysafe_house_building_id: null,
        isKeysafeHouse: false,
        elevator: false
      },
      isKeysafeHouseLinked: false
    };
  },

  computed: {
    rules() {
      return {
        requiredTextLine,
        textLine,
        textDescription,
        key_code: [maxLength(15)],
        accessDescription: [maxLength(1000), notEmoji],
        requiredSelectNotArray,
        street: [...requiredTextLine, this.limitIsAchieved],
        keysafe_description: [...textDescription, this.limitIsAchieved]
      };
    },

    limitIsAchieved() {
      let sum = 0;

      if (this.form.isKeysafeHouse) {
        try {
          sum += this.form.street.length;
          sum += this.form.keysafe_description.length;
        } catch {
          sum += 0;
        }
      }

      return () =>
        sum < 58 ||
        this.$t('MESSAGE.DATA_FROM_STREET_DESCRIPTION_LIMIT_LENGTH');
    }
  },

  watch: {
    'form.keysafe_house_building_id': {
      deep: true,
      immediate: true,
      handler() {
        this.checkIsKeysafeHouse();
      }
    },
    'form.isKeysafeHouse': {
      deep: true,
      immediate: true,
      handler() {
        this.isKeySafeHouseHandler();
      }
    }
  },

  methods: {
    checkIsKeysafeHouse() {
      if (!(this.value && this.value.id)) {
        return;
      }

      if (this.value.id === this.value.keysafe_house_building_id) {
        this.form.isKeysafeHouse = true;
      }

      if (this.keysafeHousesLinked.has(this.value.id)) {
        this.isKeysafeHouseLinked = true;
      }
    },

    isKeySafeHouseHandler() {
      this.validateStreet();
      if (this.form.isKeysafeHouse) {
        this.validateKeySafeDescr();
        this.form.keysafe_house_building_id = this.form.id;
      }
      if (!this.form.isKeysafeHouse) {
        this.form.keysafe_description = null;
        this.form.keysafe_house_building_id = null;
      }
      this.updateValue(
        'keysafe_house_building_id',
        this.form.keysafe_house_building_id
      );
    },
    validateStreet() {
      if (this.$refs.street) {
        this.$nextTick(() => {
          this.$refs.street.validate();
        });
      }
    },
    validateKeySafeDescr() {
      if (this.$refs.keysafe_description) {
        this.$nextTick(() => {
          this.$refs.keysafe_description.validate();
        });
      }
    }
  }
};
</script>

<style scoped></style>
